import { useEffect, useMemo, useState } from "react";
import { CalendarDays, ExternalLink, Eye, EyeOff, Image, MessageSquare, Plus, RefreshCw, Save, Trash2, Upload, Users } from "lucide-react";
import { API, api } from "../lib/api";

const blankEvent = {
  title: "",
  description: "",
  header_image_url: "",
  starts_at: "",
  ends_at: "",
  share_slug: "",
  published: true,
};

const inputClass = "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";
const textareaClass = "w-full min-h-[140px] px-3 py-2 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";

function toInputDate(value) {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  } catch (err) {
    return "";
  }
}

function fromInputDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function formFromEvent(event) {
  if (!event) return blankEvent;
  return {
    title: event.title || "",
    description: event.description || "",
    header_image_url: event.header_image_url || "",
    starts_at: toInputDate(event.starts_at),
    ends_at: toInputDate(event.ends_at),
    share_slug: event.share_slug || "",
    published: event.published !== false,
  };
}

function payloadFromForm(form) {
  return {
    title: form.title,
    description: form.description,
    header_image_url: form.header_image_url,
    starts_at: fromInputDate(form.starts_at),
    ends_at: form.ends_at ? fromInputDate(form.ends_at) : null,
    share_slug: form.share_slug,
    published: Boolean(form.published),
  };
}

function mediaUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("http")) return raw;
  if (raw.startsWith("/api/")) return `${API.replace(/\/api$/, "")}${raw}`;
  if (raw.startsWith("runtime-media/")) return `${API}/runtime-media/${raw.replace("runtime-media/", "")}`;
  return raw;
}

function publicEventUrl(event) {
  const path = event?.share_slug ? `/events/${event.share_slug}` : event?.share_url || "/events";
  return `${window.location.origin}${path}`;
}

function formatDate(value) {
  if (!value) return "No date";
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
  } catch (err) {
    return value;
  }
}

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [createForm, setCreateForm] = useState(blankEvent);
  const [editForm, setEditForm] = useState(blankEvent);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const selected = useMemo(
    () => events.find((event) => event.event_id === selectedId) || events[0] || null,
    [events, selectedId]
  );

  useEffect(() => {
    setEditForm(formFromEvent(selected));
  }, [selected]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/community-events");
      const next = data.events || [];
      setEvents(next);
      setSelectedId((current) => next.find((event) => event.event_id === current)?.event_id || next[0]?.event_id || "");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const replaceEvent = (updated) => {
    setEvents((current) => {
      const exists = current.some((event) => event.event_id === updated.event_id);
      if (!exists) return [...current, updated].sort((a, b) => String(a.starts_at || "").localeCompare(String(b.starts_at || "")));
      return current.map((event) => (event.event_id === updated.event_id ? updated : event));
    });
    setSelectedId(updated.event_id);
  };

  const runAction = async (label, action) => {
    setBusy(label);
    setError("");
    setMessage("");
    try {
      const updated = await action();
      if (updated) replaceEvent(updated);
    } catch (err) {
      setError(err.response?.data?.detail || "Event action failed.");
    } finally {
      setBusy("");
    }
  };

  const createEvent = async (event) => {
    event.preventDefault();
    await runAction("create", async () => {
      const { data } = await api.post("/admin/community-events", payloadFromForm(createForm));
      setCreateForm(blankEvent);
      setMessage("Event created.");
      return data;
    });
  };

  const saveEvent = async () => {
    if (!selected) return;
    await runAction("save", async () => {
      const { data } = await api.patch(`/admin/community-events/${selected.event_id}`, payloadFromForm(editForm));
      setMessage("Event saved.");
      return data;
    });
  };

  const deleteEvent = async () => {
    if (!selected) return;
    const ok = window.confirm(`Delete "${selected.title}"? This also removes RSVPs and comments for the event.`);
    if (!ok) return;
    setBusy("delete");
    setError("");
    setMessage("");
    try {
      await api.delete(`/admin/community-events/${selected.event_id}`);
      setMessage("Event deleted.");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not delete event.");
    } finally {
      setBusy("");
    }
  };

  const uploadHeaderImage = async (file, target) => {
    if (!file) return;
    setBusy(`upload-${target}`);
    setError("");
    setMessage("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/runtime-media/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const nextValue = data.media_reference || data.media_url || "";
      if (target === "create") {
        setCreateForm((current) => ({ ...current, header_image_url: nextValue }));
      } else {
        setEditForm((current) => ({ ...current, header_image_url: nextValue }));
      }
      setMessage("Header image uploaded. Save the event to keep it attached.");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not upload header image.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-events-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Creator events</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[hsl(var(--primary))]" />
            Events
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
            Create public community events with a header image, date, description, share link, RSVPs, and viewer comments.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading || !!busy}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {(error || message) && (
        <div className={`rounded border px-4 py-3 text-sm ${error ? "border-red-500/40 bg-red-500/10 text-red-100" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"}`}>
          {error || message}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[22rem_1fr] gap-6">
        <aside className="space-y-4">
          <form onSubmit={createEvent} className="surface p-5 space-y-4">
            <div>
              <div className="label-eyebrow">New event</div>
              <h2 className="text-lg font-semibold text-white mt-1">Add to public events</h2>
            </div>
            <Field label="Title">
              <input className={inputClass} value={createForm.title} onChange={(event) => setCreateForm({ ...createForm, title: event.target.value })} required maxLength={120} />
            </Field>
            <Field label="Date and time">
              <input type="datetime-local" className={inputClass} value={createForm.starts_at} onChange={(event) => setCreateForm({ ...createForm, starts_at: event.target.value })} required />
            </Field>
            <Field label="Event link name">
              <input className={inputClass} value={createForm.share_slug} onChange={(event) => setCreateForm({ ...createForm, share_slug: event.target.value })} placeholder="dune-race" maxLength={80} />
            </Field>
            <Field label="Header image">
              <HeaderImageInput
                value={createForm.header_image_url}
                onChange={(value) => setCreateForm({ ...createForm, header_image_url: value })}
                onUpload={(file) => uploadHeaderImage(file, "create")}
                busy={busy === "upload-create"}
              />
            </Field>
            <Field label="Description">
              <textarea className={textareaClass} value={createForm.description} onChange={(event) => setCreateForm({ ...createForm, description: event.target.value })} maxLength={4000} />
            </Field>
            <button disabled={busy === "create"} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded bg-[hsl(var(--primary))] text-white font-medium hover:brightness-110 disabled:opacity-50">
              <Plus className="w-4 h-4" /> Create Event
            </button>
          </form>

          <div className="surface p-4">
            <div className="label-eyebrow mb-3">Event list</div>
            {loading ? (
              <div className="text-sm text-[hsl(var(--muted-foreground))]">Loading events...</div>
            ) : events.length === 0 ? (
              <div className="text-sm text-[hsl(var(--muted-foreground))]">No events created yet.</div>
            ) : (
              <div className="space-y-2">
                {events.map((event) => (
                  <button
                    key={event.event_id}
                    type="button"
                    onClick={() => setSelectedId(event.event_id)}
                    className={`w-full text-left rounded border p-3 transition ${selected?.event_id === event.event_id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10" : "border-[hsl(var(--border))] bg-black/30 hover:border-[hsl(var(--primary))]/60"}`}
                  >
                    <div className="flex items-center gap-2">
                      {event.published ? <Eye className="w-4 h-4 text-emerald-300" /> : <EyeOff className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />}
                      <span className="font-medium text-white truncate">{event.title}</span>
                    </div>
                    <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{formatDate(event.starts_at)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        <main className="space-y-5">
          {!selected ? (
            <div className="surface p-8 text-sm text-[hsl(var(--muted-foreground))]">Create or select an event to edit.</div>
          ) : (
            <>
              <section className="surface p-5 sm:p-6 space-y-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="label-eyebrow">{selected.published ? "Published" : "Draft"}</div>
                    <h2 className="text-xl font-semibold text-white mt-1">Edit event</h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={publicEventUrl(selected)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[hsl(var(--border))] text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] hover:text-white"
                    >
                      Share page <ExternalLink className="w-4 h-4" />
                    </a>
                    <button onClick={deleteEvent} disabled={!!busy} className="inline-flex items-center gap-2 px-3 py-2 rounded border border-red-500/40 text-xs uppercase tracking-widest text-red-200 hover:bg-red-500/10 disabled:opacity-50">
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Field label="Title">
                    <input className={inputClass} value={editForm.title} onChange={(event) => setEditForm({ ...editForm, title: event.target.value })} maxLength={120} />
                  </Field>
                  <Field label="Header image URL">
                    <HeaderImageInput
                      value={editForm.header_image_url}
                      onChange={(value) => setEditForm({ ...editForm, header_image_url: value })}
                      onUpload={(file) => uploadHeaderImage(file, "edit")}
                      busy={busy === "upload-edit"}
                    />
                  </Field>
                  <Field label="Starts at">
                    <input type="datetime-local" className={inputClass} value={editForm.starts_at} onChange={(event) => setEditForm({ ...editForm, starts_at: event.target.value })} />
                  </Field>
                  <Field label="Ends at optional">
                    <input type="datetime-local" className={inputClass} value={editForm.ends_at} onChange={(event) => setEditForm({ ...editForm, ends_at: event.target.value })} />
                  </Field>
                  <Field label="Event link name">
                    <input className={inputClass} value={editForm.share_slug} onChange={(event) => setEditForm({ ...editForm, share_slug: event.target.value })} placeholder="dune-race" maxLength={80} />
                    <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))] break-all">
                      Share URL: {selected ? publicEventUrl({ ...selected, share_slug: editForm.share_slug || selected.share_slug }) : "/events"}
                    </div>
                  </Field>
                  <label className="flex items-center gap-3 rounded border border-[hsl(var(--border))] bg-black/30 px-3 py-3 text-sm">
                    <input type="checkbox" checked={editForm.published} onChange={(event) => setEditForm({ ...editForm, published: event.target.checked })} className="w-4 h-4" />
                    Published on public Events page
                  </label>
                </div>

                <Field label="Description">
                  <textarea className={textareaClass} value={editForm.description} onChange={(event) => setEditForm({ ...editForm, description: event.target.value })} maxLength={4000} />
                </Field>

                <button onClick={saveEvent} disabled={busy === "save"} className="inline-flex items-center gap-2 px-4 py-3 rounded bg-[hsl(var(--primary))] text-white font-medium hover:brightness-110 disabled:opacity-50">
                  <Save className="w-4 h-4" /> Save Event
                </button>
              </section>

              <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <InfoCard icon={Users} label="Going" value={selected.rsvp_counts?.attending || 0} />
                <InfoCard icon={Users} label="Tentative" value={selected.rsvp_counts?.tentative || 0} />
                <InfoCard icon={MessageSquare} label="Comments" value={(selected.comments || []).length} />
              </section>

              <section className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div className="surface p-5">
                  <div className="label-eyebrow mb-3">RSVPs</div>
                  {(selected.rsvps || []).length === 0 ? (
                    <div className="text-sm text-[hsl(var(--muted-foreground))]">No RSVPs yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {selected.rsvps.map((rsvp, index) => (
                        <div key={`${rsvp.login}-${index}`} className="rounded border border-[hsl(var(--border))] bg-black/35 px-3 py-2">
                          <div className="font-medium text-white">{rsvp.display_name}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">@{rsvp.login} - {rsvp.status}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="surface p-5">
                  <div className="label-eyebrow mb-3">Comments</div>
                  {(selected.comments || []).length === 0 ? (
                    <div className="text-sm text-[hsl(var(--muted-foreground))]">No comments yet.</div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                      {selected.comments.map((comment) => (
                        <div key={comment.id} className="rounded border border-[hsl(var(--border))] bg-black/35 px-3 py-2">
                          <div className="font-medium text-white">{comment.display_name}</div>
                          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] whitespace-pre-wrap">{comment.body}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function Field({ label = "", children } = {}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function HeaderImageInput({ value, onChange, onUpload, busy }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          className={inputClass}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Upload an image or paste https://..."
          maxLength={500}
        />
        <label className="inline-flex items-center justify-center gap-2 h-10 px-3 rounded border border-[hsl(var(--border))] text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] hover:text-white hover:border-[hsl(var(--primary))] cursor-pointer whitespace-nowrap">
          <Upload className="w-4 h-4" /> {busy ? "Uploading" : "Upload"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) onUpload(file);
            }}
          />
        </label>
      </div>
      {value ? (
        <div className="rounded border border-[hsl(var(--border))] bg-black/35 p-2">
          <img src={mediaUrl(value)} alt="" className="w-full max-h-44 object-cover rounded" />
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
            <Image className="w-3 h-3" />
            <span className="break-all">{value}</span>
          </div>
        </div>
      ) : (
        <div className="text-xs text-[hsl(var(--muted-foreground))]">
          Uploaded images appear in Files. Remove the event header or delete the event before deleting the image.
        </div>
      )}
    </div>
  );
}

function InfoCard({ icon: Icon, label = "", value } = {}) {
  return (
    <div className="surface p-4">
      <Icon className="w-5 h-5 text-[hsl(var(--primary))] mb-3" strokeWidth={1.5} />
      <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="mt-1 text-3xl font-semibold text-white">{value}</div>
    </div>
  );
}
